// DE configuration routes (DC-01 contracts; 02 §DE configuration, 05 §DE configuration;
// ADR-0018 bundle contract, ADR-0019 verdict gate, ADR-0020 as widened by the founder on
// 2026-10-20 to person and company model credentials). Handlers, by package (program §6.2):
// ADI-02 launch + AiGentPanel callbacks; DC-02 validation; DC-03 catalog and authoring; DC-04
// deploys, company approval and template overrides; DC-05 verification override; DC-06 model
// credentials; DC-07 verdicts, decision log, premise, resume; DC-09 edit proposals; DC-10 drift
// audits; DC-11 self-serve deploy. The agency console (`/v1/operator`) is the done-for-you
// service and base plan; company-side agent routes are the `agents` add-on (ADR-0016).
import { z } from "zod";
import { AgentTemplate, Run } from "../agent-rows";
import {
  AgentTemplateVersion,
  ConfigJson,
  ConfigurationBundle,
  Deploy,
  DeployError,
  DeTemplateOverride,
  DriftAudit,
  MistakeRef,
  ModelCredential,
  PanelRef,
  PointerRef,
  RoleModelMap,
  RunDecision,
  RunRef,
  Sha256Hex,
  ShortNote,
  SubscriptionAttestation,
  TemplateEditProposal,
  TemplateEval,
  TemplateRule,
  VerificationOverride,
} from "../de-config-rows";
import {
  AgentTemplateKind,
  AgentTemplateScope,
  CliLoginLocation,
  DeployState,
  EnforcerKind,
  ModelCredentialScope,
  RunDecisionResult,
  RunEscalationKind,
  TemplateClass,
  TemplateEditState,
  TemplateEvalKind,
  Verdict,
  VerificationState,
} from "../enums";
import { boundedRecord, LIMITS, NonEmpty, Slug, Timestamp, text, Uuid } from "../primitives";
import { RoleDefaultsInput } from "../template-rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const op = "/v1/operator";
const co = "/v1/companies/{co}";
const console_ = { feature: "core", tag: "agency-console" } as const;
const agents = { feature: "agents" } as const;
/**
 * BYO-01: a person's own model credentials are an account setting ("My subscriptions"), so the
 * routes are base plan, as their events already were (`deConfig` in events.ts). Using one for a
 * run still needs the `agents` add-on (admission), the platform switch and the company flag.
 */
const credentials = { feature: "core" } as const;
const Ref = z.string().min(1).max(512);

// Catalog and authoring (DC-02, DC-03, DC-09) --------------------------------------------------

export const TemplateParams = z.object({ template_id: Uuid });
export const VersionParams = z.object({ version_id: Uuid });
export const ProposalParams = z.object({ proposal_id: Uuid });

export const AgentTemplateListQuery = PageQuery.extend({
  template_class: TemplateClass.optional(),
});
export const AgentTemplateCreate = z
  .strictObject({
    scope: AgentTemplateScope,
    agency_id: Uuid.optional().describe("`agency` scope only"),
    key: Slug,
    name: NonEmpty,
    kind: AgentTemplateKind,
    template_class: TemplateClass.default("managed"),
  })
  .meta({ id: "AgentTemplateCreate" });

const EvalInput = z.strictObject({
  key: text(LIMITS.key),
  kind: TemplateEvalKind,
  spec_ref: Ref,
  proves_mistake_ref: MistakeRef.optional(),
});
const RuleInput = z
  .strictObject({
    key: text(LIMITS.key),
    rule_text: text(LIMITS.text),
    enforcer_kind: EnforcerKind,
    enforcer_ref: Ref,
    proven_against_ref: MistakeRef,
    proven_at: Timestamp.optional(),
    reason_if_written: z.string().max(LIMITS.text).optional(),
  })
  .refine((r) => r.enforcer_kind !== "written_rule" || !!r.reason_if_written?.trim(), {
    message: "a written_rule needs reason_if_written (why nothing stronger applies)",
    path: ["reason_if_written"],
  });

/** A draft version: everything editable until `validate` freezes it. */
export const TemplateVersionDraft = z
  .strictObject({
    supersedes_version_id: Uuid.optional(),
    skill_index_ref: Ref.optional(),
    skill_index_token_cap: z.int().min(1).max(32_000).optional().describe("Default 2000"),
    verification_skill_pack_id: Uuid.optional(),
    role_model_map: RoleModelMap,
    standing_orders_text: text(LIMITS.longText),
    tool_grants: ConfigJson.optional(),
    runtime_config: ConfigJson.optional(),
    changelog: z.string().max(LIMITS.text).optional(),
    evals: z.array(EvalInput).max(LIMITS.list).default([]),
    rules: z.array(RuleInput).max(LIMITS.list).default([]),
    /** TEMPLATES-T2: a role template's defaults (checked in full at validation). */
    role_defaults: RoleDefaultsInput.optional(),
  })
  .meta({ id: "TemplateVersionDraft" });
/** PATCH a draft; `evals` and `rules`, when sent, replace the whole set. */
export const TemplateVersionPatch = z
  .strictObject({
    skill_index_ref: Ref.nullable().optional(),
    skill_index_token_cap: z.int().min(1).max(32_000).optional(),
    verification_skill_pack_id: Uuid.nullable().optional(),
    role_model_map: RoleModelMap.optional(),
    standing_orders_text: text(LIMITS.longText).optional(),
    tool_grants: ConfigJson.optional(),
    runtime_config: ConfigJson.optional(),
    changelog: z.string().max(LIMITS.text).nullable().optional(),
    evals: z.array(EvalInput).max(LIMITS.list).optional(),
    rules: z.array(RuleInput).max(LIMITS.list).optional(),
    /** TEMPLATES-T2: null makes the draft an ordinary (non-role) template again. */
    role_defaults: RoleDefaultsInput.nullable().optional(),
  })
  .meta({ id: "TemplateVersionPatch" });
export const TemplateVersionDetail = z
  .object({
    version: AgentTemplateVersion,
    evals: z.array(TemplateEval),
    rules: z.array(TemplateRule),
  })
  .meta({ id: "TemplateVersionDetail" });
export const AgentTemplateDetail = z
  .object({ template: AgentTemplate, versions: z.array(AgentTemplateVersion) })
  .meta({ id: "AgentTemplateDetail" });
export const TemplateVersionDeprecate = z
  .strictObject({
    replaced_by_version_id: Uuid.optional(),
    reason: z.string().max(LIMITS.text).optional(),
  })
  .meta({ id: "TemplateVersionDeprecate" });

export const TemplateEditProposalListQuery = PageQuery.extend({
  template_id: Uuid.optional(),
  state: TemplateEditState.optional(),
});
/** A person's proposal; the reflection routine (`job:template_edit.reflect`) writes its own. */
export const TemplateEditProposalCreate = z
  .strictObject({
    template_id: Uuid,
    based_on_version_id: Uuid,
    diff_ref: Ref,
    eval_id: Uuid.describe("Must fail on based_on and pass on the new version"),
    run_refs: z.array(RunRef).max(LIMITS.list).default([]),
  })
  .meta({ id: "TemplateEditProposalCreate" });
export const TemplateEditReject = z
  .strictObject({ reason: text(LIMITS.text) })
  .meta({ id: "TemplateEditReject" });
export const TemplateEditAccepted = z
  .object({ proposal: TemplateEditProposal, version: AgentTemplateVersion })
  .meta({ id: "TemplateEditAccepted" });

// Deploys (DC-04, DC-05, DC-11) -----------------------------------------------------------------

export const DeployParams = z.object({ deploy_id: Uuid });
export const DeployListQuery = PageQuery.extend({
  company_id: Uuid.optional(),
  state: DeployState.optional(),
});
export const CompanyDeployListQuery = PageQuery.extend({ state: DeployState.optional() });

/**
 * Stage a `done_for_you` deploy (ADR-0018 §5): the server builds the bundle from the published
 * version and the DE's company override, derives `kind` (`first` unless this version is already
 * confirmed in the company), and stages it.
 */
export const DeployStage = z
  .strictObject({
    target_company_id: Uuid,
    de_membership_id: Uuid,
    template_version_id: Uuid,
    rollback: z.boolean().optional().describe("To a version already confirmed in this company"),
  })
  .meta({ id: "DeployStage" });
export const DeployDetail = z
  .object({
    deploy: Deploy,
    /** Null while the deploy waits for its verification override (DC-05), which builds it. */
    bundle: ConfigurationBundle.nullable(),
    verification_override: VerificationOverride.nullable(),
    /**
     * DC-05: the deploy's version has no verification skill, so it reaches AiGentPanel only
     * under an operator's pilot override (`bundle` is null until then).
     */
    awaits_verification_override: z.boolean(),
    /**
     * AGENT-POLISH: the AiGentPanel profile the DE runs under: the one stored on its first hand-off,
     * or (before any) the readable `<company>-<role>-<name>-<shortid>` the hand-off will send.
     */
    profile_name: z.string().nullable().optional(),
  })
  .meta({ id: "DeployDetail" });
export const DeployReject = z
  .strictObject({ reason: text(LIMITS.text) })
  .meta({ id: "DeployReject" });
/**
 * A deploy as the target company sees it (DC-05 company screens): the row, the template's name
 * and version number (a company cannot read the agency catalog), and the pilot's verification
 * override, so whoever approves sees that it ships **unverified**.
 */
export const CompanyDeploy = Deploy.extend({
  template_name: z.string(),
  version: z.int().min(1),
  verification_override: VerificationOverride.nullable(),
  /**
   * DC-11: the bundle's verification state (null while a pilot waits for its override), so an
   * empty self-serve agent's deploy shows **unverified** before the DE mirror does.
   */
  verification_state: VerificationState.nullable(),
}).meta({ id: "CompanyDeploy" });
export const VerificationOverrideCreate = z
  .strictObject({ reason: text(LIMITS.text), pilot_until: Timestamp })
  .meta({ id: "VerificationOverrideCreate" });

/** The company's override: standing orders and/or the role-to-model map (founder, Q43). */
export const DeTemplateOverridePut = z
  .strictObject({
    /** DE-WIZARD: null = the DE's own, before any template is deployed or staged. */
    template_id: Uuid.nullable(),
    standing_orders_text: text(LIMITS.longText).nullable().optional(),
    role_model_map: RoleModelMap.nullable().optional(),
  })
  .refine((o) => o.standing_orders_text != null || o.role_model_map != null, {
    message: "override at least one of standing_orders_text and role_model_map",
  })
  .meta({ id: "DeTemplateOverridePut" });
export const DeTemplateOverrideView = z
  .object({
    override: DeTemplateOverride.nullable(),
    /**
     * The template the DE runs now (its template version's), else null: when `override` is
     * null, the editor starts this template's row (or, null, the DE's own template-less row).
     */
    current_template_id: Uuid.nullable(),
  })
  .meta({ id: "DeTemplateOverrideView" });

/** Self-serve (`agents` add-on): an empty agent or a basic template, with no approval state. */
export const SelfServeDeploy = z
  .strictObject({
    de_membership_id: Uuid,
    template_version_id: Uuid.describe("The platform's `empty` version or a basic template's"),
    standing_orders_text: text(LIMITS.longText).optional(),
    role_model_map: RoleModelMap.optional().describe(
      "Gateway paths only (the subscription path is refused)",
    ),
  })
  .meta({ id: "SelfServeDeploy" });
export const SelfServeTemplate = z
  .object({ template: AgentTemplate, version: AgentTemplateVersion })
  .meta({ id: "SelfServeTemplate" });

// AiGentPanel (ADI-02; ADR-0018 §2, §4) --------------------------------------------------------

export const AigentpanelLaunch = z
  .strictObject({ company_id: Uuid, de_membership_id: Uuid.optional() })
  .meta({ id: "AigentpanelLaunch" });
export const AigentpanelLaunchUrl = z
  .object({ url: z.url(), expires_at: Timestamp })
  .meta({ id: "AigentpanelLaunchUrl" });
const StackHealthValue = z.enum(["ok", "degraded", "down", "asleep"]);
const StackCount = z.int().min(0).max(10_000);
/**
 * SCREENS-S1 (docs/design/2026-10-28-screens-stack-contract.md §1): a company stack in
 * `stack.registered` (every field) and `stack.health` (slug, credential_ref, health, capacity,
 * retired). The credential is a reference only; its value is never in a callback.
 */
/**
 * PROVISION-IN-PRODUCT (the wake intake): a self-hosted DE's bridge, registered by the satellite
 * when it starts the DE's bridge listener (replaces the hand-written `bridge-wake.json`). `secret`
 * is the push secret the bridge verifies (≥ 32 characters; sealed on receipt, never returned);
 * `container` / `profile` are where the DE runs (recorded as its runtime placement).
 */
export const AigentpanelBridge = z
  .strictObject({
    company_id: Uuid,
    membership_id: Uuid,
    url: z.string().min(8).max(2000).optional(),
    secret: z.string().min(32).max(512).optional(),
    container: z.string().min(1).max(256).nullable().optional(),
    profile: z.string().min(1).max(256).nullable().optional(),
  })
  .meta({ id: "AigentpanelBridge" });

export const AigentpanelStack = z
  .strictObject({
    slug: z
      .string()
      .max(63)
      .regex(/^[a-z0-9][a-z0-9-]{0,62}$/),
    name: z.string().max(80).optional(),
    placement: z.enum(["shared", "dedicated"]).optional(),
    browsers_api: z.url().max(512).optional(),
    desktops_api: z.url().max(512).optional(),
    live_url: z.url().max(512).optional(),
    browser_live: z.enum(["vnc", "screenshot"]).optional(),
    credential_ref: z.strictObject({
      id: z
        .string()
        .max(64)
        .regex(/^[A-Za-z0-9_-]{1,64}$/),
      version: z.int().min(1),
    }),
    versions: boundedRecord(z.string().max(200), 20, z.string().max(40)).optional(),
    health: z.strictObject({
      browsers: StackHealthValue,
      desktops: StackHealthValue,
      live: StackHealthValue,
    }),
    capacity: z.strictObject({
      browsers_max: StackCount,
      browsers_used: StackCount,
      desktops_max: StackCount,
      desktops_used: StackCount,
    }),
    retired: z.boolean().optional(),
    /** `stack.health`: the operator's presence per lease (watchers incl. controllers, control). */
    leases: z
      .array(
        z.strictObject({
          lease_id: z.string().max(64),
          operator: z.strictObject({ viewing: StackCount, control: z.boolean() }),
        }),
      )
      .max(500)
      .optional(),
  })
  .meta({ id: "AigentpanelStack" });
export type AigentpanelStack = z.infer<typeof AigentpanelStack>;

/**
 * SATELLITE-REGISTRY (dispatcher design §2 "ClawPanel"): the panel registers the host it
 * provisioned and retires it; `node_id` is the panel's id of the host. Owner null: the
 * platform's own capacity.
 */
export const AigentpanelSatellite = z
  .strictObject({
    node_id: z.string().trim().min(1).max(128),
    name: z.string().trim().min(1).max(120).optional(),
    owner_company_id: Uuid.nullable().optional(),
    transport: z.enum(["wireguard", "mtls"]).optional(),
    base_address: z.string().trim().min(1).max(255).optional(),
    max_concurrent_runs: z.int().min(1).max(1000).optional(),
    health_path: z
      .string()
      .max(121)
      .regex(/^\/[A-Za-z0-9._/-]{0,120}$/)
      .optional(),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .meta({ id: "AigentpanelSatellite" });

/**
 * SATELLITE-REGISTRY: one DE's profile the panel provisioned on a registered host
 * (`endpoint.registered`, with the profile's `API_SERVER_KEY`, sealed on receipt and never
 * echoed) or removed (`endpoint.retired`).
 */
export const AigentpanelEndpoint = z
  .strictObject({
    node_id: z.string().trim().min(1).max(128),
    company_id: Uuid,
    de_membership_id: Uuid,
    instance_id: z.string().trim().min(1).max(128).optional(),
    profile_name: z
      .string()
      .max(128)
      .regex(/^(?!\.{1,2}$)[A-Za-z0-9._-]{1,128}$/)
      .optional(),
    api_base_url: z.string().trim().min(1).max(500).optional(),
    multiplex: z.boolean().optional(),
    api_key: z.string().min(16).max(256).optional(),
    reason: z.string().trim().min(1).max(500).optional(),
  })
  .meta({ id: "AigentpanelEndpoint" });

/** The panel's signed callback (`X-AiGentPanel-Signature`); idempotent on `(deploy_id, type)`. */
export const AigentpanelCallback = z
  .strictObject({
    id: Uuid.describe("Delivery id (`X-AiGentPanel-Delivery`)"),
    type: z.enum([
      "deploy.confirmed",
      "deploy.failed",
      "launch.used",
      "stack.registered",
      "stack.health",
      // PROVISION-IN-PRODUCT: the wake intake (a DE's bridge registered or removed)
      "bridge.registered",
      "bridge.removed",
      // SATELLITE-REGISTRY
      "satellite.registered",
      "satellite.retired",
      "endpoint.registered",
      "endpoint.retired",
    ]),
    deploy_id: Uuid.nullable().optional(),
    jti: Uuid.nullable().optional().describe("`launch.used` only"),
    bundle_sha: Sha256Hex.nullable().optional(),
    panel_ref: PanelRef.nullable().optional(),
    error: DeployError.nullable().optional(),
    stack: AigentpanelStack.optional().describe("`stack.registered` / `stack.health` only"),
    bridge: AigentpanelBridge.optional().describe("`bridge.registered` / `bridge.removed` only"),
    satellite: AigentpanelSatellite.optional().describe("`satellite.*` only"),
    endpoint: AigentpanelEndpoint.optional().describe("`endpoint.*` only"),
    occurred_at: Timestamp,
  })
  .refine(
    (c) =>
      c.type === "satellite.registered"
        ? c.satellite?.name != null &&
          c.satellite.transport != null &&
          c.satellite.base_address != null
        : c.type === "satellite.retired"
          ? c.satellite != null
          : c.type === "endpoint.registered"
            ? c.endpoint?.instance_id != null &&
              c.endpoint.profile_name != null &&
              c.endpoint.api_base_url != null &&
              c.endpoint.api_key != null
            : c.type === "endpoint.retired"
              ? c.endpoint != null
              : true,
    {
      message:
        "satellite.registered carries satellite {node_id, name, transport, base_address}; endpoint.registered carries endpoint {node_id, company_id, de_membership_id, instance_id, profile_name, api_base_url, api_key}",
    },
  )
  .refine(
    (c) =>
      c.type.startsWith("satellite.") || c.type.startsWith("endpoint.")
        ? true
        : c.type === "bridge.registered"
          ? c.bridge?.url != null && c.bridge.secret != null
          : c.type === "bridge.removed"
            ? c.bridge != null
            : c.type === "launch.used"
              ? c.jti != null
              : c.type === "stack.registered"
                ? c.stack != null &&
                  c.stack.name != null &&
                  c.stack.placement != null &&
                  c.stack.browsers_api != null &&
                  c.stack.desktops_api != null &&
                  c.stack.live_url != null
                : c.type === "stack.health"
                  ? c.stack != null
                  : c.deploy_id != null,
    {
      message:
        "deploy callbacks carry deploy_id; launch.used carries jti; stack.* carry stack (registered: every endpoint); bridge.* carry bridge (registered: url and secret)",
    },
  )
  .meta({ id: "AigentpanelCallback" });
export const AigentpanelCallbackAck = z
  .object({
    ok: z.literal(true),
    duplicate: z.boolean(),
    /** Phase 2: the deploy has no binding yet; the callback is kept and applied after it. */
    pending: z.boolean().optional(),
  })
  .meta({ id: "AigentpanelCallbackAck" });

// Model credentials (DC-06; ADR-0020 widened 2026-10-20) ---------------------------------------

/** What the attester confirms about the provider's terms (one live attestation per credential). */
export const AttestationInput = z
  .strictObject({
    terms_ref: Ref.describe("The provider terms page or document the attester read"),
    terms_version: z.string().max(LIMITS.key).optional(),
    plan: z.string().max(LIMITS.key).optional(),
    plan_is_own: z.boolean(),
    own_work_only: z.boolean(),
    allows_paid_client_work: z.boolean(),
  })
  .meta({ id: "AttestationInput" });
const Provider = z
  .string()
  .max(32)
  .regex(/^[a-z][a-z0-9_-]{1,31}$/);
/**
 * Add a credential. `api_key`: sealed in the keyring on receipt, used upstream by the gateway,
 * never returned. `cli_subscription`: person scope only; the login stays inside the vendor's
 * unmodified CLI on a runtime session or the person's device, and no token is ever sent here.
 */
export const ModelCredentialCreate = z
  .discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("api_key"),
      scope: ModelCredentialScope,
      provider: Provider,
      label: text(LIMITS.name),
      api_key: z.string().min(8).max(512),
      attestation: AttestationInput,
    }),
    z.strictObject({
      kind: z.literal("cli_subscription"),
      scope: z.literal("person"),
      provider: Provider,
      label: text(LIMITS.name),
      plan: text(LIMITS.key),
      login_location: CliLoginLocation,
      runtime_ref: z.string().max(LIMITS.name).optional(),
      fallback_to_gateway: z
        .boolean()
        .optional()
        .describe("Owner pre-approval to fall back to the gateway; never silent"),
      attestation: AttestationInput,
    }),
  ])
  .meta({ id: "ModelCredentialCreate" });
export const ModelCredentialCreated = z
  .object({ credential: ModelCredential, attestation: SubscriptionAttestation })
  .meta({ id: "ModelCredentialCreated" });
export const ModelCredentialListQuery = PageQuery.extend({
  scope: ModelCredentialScope.optional(),
});
export const ModelCredentialRevoke = z
  .strictObject({ reason: z.string().max(LIMITS.text).optional() })
  .meta({ id: "ModelCredentialRevoke" });

/**
 * ACTIONS: the non-secret fields of an active credential. A key never changes here (no field for
 * one): a new key is a new credential and the old one is revoked. The plan is attested, so a plan
 * change is a re-attestation. The plan-login fields are a `cli_subscription`'s only (422 else).
 */
export const ModelCredentialPatch = z
  .strictObject({
    label: text(LIMITS.name).optional(),
    login_location: CliLoginLocation.optional(),
    runtime_ref: z.string().max(LIMITS.name).nullable().optional(),
    fallback_to_gateway: z.boolean().optional(),
  })
  .refine((p) => Object.keys(p).length > 0, { message: "at least one field" })
  .meta({ id: "ModelCredentialPatch" });

// Verdicts, decision log, premise, drift audits (DC-07, DC-10) ---------------------------------

/** ADR-0019 §1: pointers only; one verdict per run, depth 0. */
export const VerdictRecord = z
  .strictObject({
    verdict: Verdict,
    output_ref: PointerRef,
    evidence_ref: PointerRef,
    note: ShortNote.optional(),
  })
  .meta({ id: "VerdictRecord" });
const DecisionInput = z.strictObject({
  seq: z.int().min(1),
  decided_at: Timestamp,
  decision: text(200),
  why: z.string().max(500),
  evidence_ref: PointerRef.optional(),
  result: RunDecisionResult,
  escalation_kind: RunEscalationKind.optional(),
});
/** ADR-0019 §5: batched ≤ 50; `seq` strictly increasing with no gap or repeat. */
export const DecisionsAppend = z
  .strictObject({ decisions: z.array(DecisionInput).min(1).max(50) })
  .meta({ id: "DecisionsAppend" });
export const DecisionsAppended = z
  .object({ data: z.array(RunDecision) })
  .meta({ id: "DecisionsAppended" });
export const PremiseEscalate = z
  .strictObject({
    assumption: text(500),
    failed_fix_refs: z.array(Uuid).length(2).describe("The two failed `run_decision` ids"),
  })
  .meta({ id: "PremiseEscalate" });
export const RunResume = z
  .strictObject({ note: z.string().max(LIMITS.text).optional() })
  .meta({ id: "RunResume" });
/** ADR-0019 §6: below this many runs with a verdict a period is `insufficient_data`. */
export const VERIFIED_RATE_MIN_RUNS = 5;
export const VerifiedRateQuery = z.object({
  months: z.coerce.number().int().min(1).max(12).optional().describe("Default 3, newest last"),
});
/**
 * `de.verified_rate` (ADR-0019 §6): verified runs ÷ runs with a verdict, per calendar month (UTC)
 * of `run.verdict_recorded`. `value` is null and `status` `insufficient_data` below
 * `min_runs` (missing is never zero).
 */
export const VerifiedRate = z
  .object({
    metric_key: z.literal("de.verified_rate"),
    membership_id: Uuid,
    min_runs: z.int().min(1),
    periods: z.array(
      z.object({
        period_key: z.string().regex(/^\d{4}-\d{2}$/),
        verified: z.int().min(0),
        with_verdict: z.int().min(0),
        value: z.number().min(0).max(1).nullable(),
        status: z.enum(["ok", "insufficient_data"]),
      }),
    ),
  })
  .meta({ id: "VerifiedRate" });
export const DriftAuditRequest = z
  .strictObject({
    trigger: z.enum(["manual", "target_changed"]),
    scheduled_for: Timestamp.optional().describe("Default: now"),
  })
  .meta({ id: "DriftAuditRequest" });

// Routes ----------------------------------------------------------------------------------------

const runPost = (id: string, path: string, summary: string) =>
  ({
    ...agents,
    id,
    method: "post",
    path: `${co}/runs/{id}/${path}`,
    tag: "runs",
    summary,
  }) as const;

export const deConfigRoutes = {
  // ADI-02
  "operator.aigentpanel.launch": defineRoute({
    ...console_,
    id: "operator.aigentpanel.launch",
    method: "post",
    path: `${op}/aigentpanel/launch`,
    summary: "One-time 60 s link into the AiGentPanel instance (agency admin only)",
    access: "operator",
    body: AigentpanelLaunch,
    response: { status: 201, schema: AigentpanelLaunchUrl },
    errors: [],
    emits: ["launch_token.minted"],
  }),
  "aigentpanel.callbacks": defineRoute({
    ...console_,
    id: "aigentpanel.callbacks",
    method: "post",
    path: "/v1/aigentpanel/callbacks",
    summary: "AiGentPanel's signed deploy and launch callbacks (HMAC, ±300 s; idempotent)",
    access: "public",
    body: AigentpanelCallback,
    response: { status: 200, schema: AigentpanelCallbackAck },
    errors: ["signature_invalid", "not_found", "conflict"],
    emits: [
      "deploy.confirmed",
      "deploy.failed",
      "launch_token.used",
      "digital_employee.panel_drift",
      // SCREENS-S1: `stack.registered` / `stack.health`
      "screen_stack.registered",
      "screen_stack.health_changed",
      "screen_lease.ended",
      "screen_ask.resolved",
      // PROVISION-IN-PRODUCT: `bridge.registered` / `bridge.removed`
      "digital_employee.bridge_registered",
      "digital_employee.bridge_removed",
    ],
  }),

  // DC-03 catalog (DC-02 validates)
  "operator.agentTemplates.list": defineRoute({
    ...console_,
    id: "operator.agentTemplates.list",
    method: "get",
    path: `${op}/agent-templates`,
    summary: "Agent templates (platform and the agency's own)",
    access: "operator",
    query: AgentTemplateListQuery,
    response: { status: 200, schema: page(AgentTemplate) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "operator.agentTemplates.create": defineRoute({
    ...console_,
    id: "operator.agentTemplates.create",
    method: "post",
    path: `${op}/agent-templates`,
    summary: "Create a template head (template_author)",
    access: "operator",
    body: AgentTemplateCreate,
    idempotency: "optional",
    response: { status: 201, schema: AgentTemplate },
    errors: ["conflict"],
    emits: ["agent_template.created"],
  }),
  "operator.agentTemplates.get": defineRoute({
    ...console_,
    id: "operator.agentTemplates.get",
    method: "get",
    path: `${op}/agent-templates/{template_id}`,
    summary: "A template with its versions",
    access: "operator",
    params: TemplateParams,
    response: { status: 200, schema: AgentTemplateDetail },
    errors: [],
    emits: [],
  }),
  "operator.templateVersions.create": defineRoute({
    ...console_,
    id: "operator.templateVersions.create",
    method: "post",
    path: `${op}/agent-templates/{template_id}/versions`,
    summary: "Cut a draft version with its evals and rule → enforcer table",
    access: "operator",
    params: TemplateParams,
    body: TemplateVersionDraft,
    idempotency: "optional",
    response: { status: 201, schema: TemplateVersionDetail },
    errors: [],
    emits: ["agent_template_version.drafted"],
  }),
  "operator.templateVersions.get": defineRoute({
    ...console_,
    id: "operator.templateVersions.get",
    method: "get",
    path: `${op}/template-versions/{version_id}`,
    summary: "A version with its evals and rules",
    access: "operator",
    params: VersionParams,
    response: { status: 200, schema: TemplateVersionDetail },
    errors: [],
    emits: [],
  }),
  "operator.templateVersions.update": defineRoute({
    ...console_,
    id: "operator.templateVersions.update",
    method: "patch",
    path: `${op}/template-versions/{version_id}`,
    summary: "Edit a draft (a validated version is immutable)",
    access: "operator",
    params: VersionParams,
    body: TemplateVersionPatch,
    response: { status: 200, schema: TemplateVersionDetail },
    errors: ["version_not_draft"],
    emits: ["agent_template_version.draft_updated"],
  }),
  "operator.templateVersions.validate": defineRoute({
    ...console_,
    id: "operator.templateVersions.validate",
    method: "post",
    path: `${op}/template-versions/{version_id}/validate`,
    summary: "Validate a draft: token cap, verification skill, evals, rules (freezes it on pass)",
    access: "operator",
    params: VersionParams,
    idempotency: "optional",
    response: { status: 200, schema: TemplateVersionDetail },
    errors: ["version_not_draft"],
    emits: ["agent_template_version.validated", "agent_template_version.validation_failed"],
  }),
  "operator.templateVersions.publish": defineRoute({
    ...console_,
    id: "operator.templateVersions.publish",
    method: "post",
    path: `${op}/template-versions/{version_id}/publish`,
    summary: "Publish a validated version (refused without a verification skill or a passing eval)",
    access: "operator",
    params: VersionParams,
    idempotency: "optional",
    response: { status: 200, schema: AgentTemplateVersion },
    errors: ["publish_refused"],
    emits: ["agent_template_version.published"],
  }),
  "operator.templateVersions.deprecate": defineRoute({
    ...console_,
    id: "operator.templateVersions.deprecate",
    method: "post",
    path: `${op}/template-versions/{version_id}/deprecate`,
    summary: "Deprecate a published version (re-deploy tasks on affected supervisor seats)",
    access: "operator",
    params: VersionParams,
    body: TemplateVersionDeprecate,
    idempotency: "optional",
    response: { status: 200, schema: AgentTemplateVersion },
    errors: ["conflict"],
    emits: ["agent_template_version.deprecated"],
  }),
  "operator.templateEditProposals.list": defineRoute({
    ...console_,
    id: "operator.templateEditProposals.list",
    method: "get",
    path: `${op}/template-edit-proposals`,
    summary: "Learning-loop edit proposals",
    access: "operator",
    query: TemplateEditProposalListQuery,
    response: { status: 200, schema: page(TemplateEditProposal) },
    errors: ["invalid_cursor"],
    emits: [],
  }),

  // DC-09 learning loop
  "operator.templateEditProposals.create": defineRoute({
    ...console_,
    id: "operator.templateEditProposals.create",
    method: "post",
    path: `${op}/template-edit-proposals`,
    summary: "Propose an edit with its eval (freeform improvement passes are refused)",
    access: "operator",
    body: TemplateEditProposalCreate,
    idempotency: "optional",
    response: { status: 201, schema: TemplateEditProposal },
    errors: ["edit_eval_required"],
    emits: ["template_edit.proposed"],
  }),
  "operator.templateEditProposals.accept": defineRoute({
    ...console_,
    id: "operator.templateEditProposals.accept",
    method: "post",
    path: `${op}/template-edit-proposals/{proposal_id}/accept`,
    summary: "Accept: cuts a new draft version (source learning_loop) to validate and publish",
    access: "operator",
    params: ProposalParams,
    idempotency: "optional",
    response: { status: 200, schema: TemplateEditAccepted },
    errors: ["edit_eval_required", "conflict"],
    emits: ["template_edit.accepted", "agent_template_version.drafted"],
  }),
  "operator.templateEditProposals.reject": defineRoute({
    ...console_,
    id: "operator.templateEditProposals.reject",
    method: "post",
    path: `${op}/template-edit-proposals/{proposal_id}/reject`,
    summary: "Reject an edit with a reason",
    access: "operator",
    params: ProposalParams,
    body: TemplateEditReject,
    idempotency: "optional",
    response: { status: 200, schema: TemplateEditProposal },
    errors: ["conflict"],
    emits: ["template_edit.rejected"],
  }),

  // DC-04 deploys
  "operator.deploys.list": defineRoute({
    ...console_,
    id: "operator.deploys.list",
    method: "get",
    path: `${op}/deploys`,
    summary: "Deploys (agency operators; the Deployment engineer's key reads its own stagings)",
    access: "operator_or_agent_key",
    query: DeployListQuery,
    response: { status: 200, schema: page(Deploy) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "operator.deploys.get": defineRoute({
    ...console_,
    id: "operator.deploys.get",
    method: "get",
    path: `${op}/deploys/{deploy_id}`,
    summary: "A deploy with its bundle and any verification override",
    access: "operator_or_agent_key",
    params: DeployParams,
    response: { status: 200, schema: DeployDetail },
    errors: [],
    emits: [],
  }),
  "operator.deploys.stage": defineRoute({
    ...console_,
    id: "operator.deploys.stage",
    method: "post",
    path: `${op}/deploys`,
    summary: "Build the bundle and stage a done-for-you deploy (Deployment engineer or operator)",
    access: "operator_or_agent_key",
    body: DeployStage,
    idempotency: "required",
    response: { status: 201, schema: DeployDetail },
    errors: [
      "bundle_refused",
      "deploy_already_open",
      "container_owned",
      "container_not_imported",
      "profile_name_conflict",
    ],
    emits: ["deploy.staged"],
  }),
  "operator.deploys.approve": defineRoute({
    ...console_,
    id: "operator.deploys.approve",
    method: "post",
    path: `${op}/deploys/{deploy_id}/approve`,
    summary: "Approve the first deploy of a version into a company (a person, never a DE)",
    access: "operator",
    params: DeployParams,
    idempotency: "optional",
    response: { status: 200, schema: Deploy },
    errors: ["deploy_state_conflict"],
    emits: ["deploy.approved"],
  }),
  "operator.deploys.reject": defineRoute({
    ...console_,
    id: "operator.deploys.reject",
    method: "post",
    path: `${op}/deploys/{deploy_id}/reject`,
    summary: "Reject a staged deploy with a reason",
    access: "operator",
    params: DeployParams,
    body: DeployReject,
    idempotency: "optional",
    response: { status: 200, schema: Deploy },
    errors: ["deploy_state_conflict"],
    emits: ["deploy.rejected"],
  }),
  "deploys.list": defineRoute({
    feature: "core",
    id: "deploys.list",
    method: "get",
    path: `${co}/deploys`,
    tag: "deploys",
    summary: "Deploys into this company (owner approves first deploys here)",
    access: "manager",
    params: CompanyParams,
    query: CompanyDeployListQuery,
    response: { status: 200, schema: page(CompanyDeploy) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "deploys.get": defineRoute({
    feature: "core",
    id: "deploys.get",
    method: "get",
    path: `${co}/deploys/{id}`,
    tag: "deploys",
    summary: "A deploy into this company",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: CompanyDeploy },
    errors: [],
    emits: [],
  }),
  "deploys.approve": defineRoute({
    feature: "core",
    id: "deploys.approve",
    method: "post",
    path: `${co}/deploys/{id}/approve`,
    tag: "deploys",
    summary: "The company owner approves the first deploy of a version",
    access: "manager",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Deploy },
    errors: ["deploy_state_conflict"],
    emits: ["deploy.approved"],
  }),
  "deploys.reject": defineRoute({
    feature: "core",
    id: "deploys.reject",
    method: "post",
    path: `${co}/deploys/{id}/reject`,
    tag: "deploys",
    summary: "The company owner rejects a staged deploy",
    access: "manager",
    params: CompanyItemParams,
    body: DeployReject,
    idempotency: "optional",
    response: { status: 200, schema: Deploy },
    errors: ["deploy_state_conflict"],
    emits: ["deploy.rejected"],
  }),
  "digitalEmployees.templateOverride": defineRoute({
    feature: "core",
    id: "digitalEmployees.templateOverride",
    method: "get",
    path: `${co}/digital-employees/{id}/template-override`,
    tag: "digital-employees",
    summary: "The company's standing orders and role-to-model map override for this DE",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: DeTemplateOverrideView },
    errors: [],
    emits: [],
  }),
  "digitalEmployees.putTemplateOverride": defineRoute({
    feature: "core",
    id: "digitalEmployees.putTemplateOverride",
    method: "put",
    path: `${co}/digital-employees/{id}/template-override`,
    tag: "digital-employees",
    summary: "Set the override (admin or the seat's supervisor); takes effect by a new deploy",
    access: "manager",
    params: CompanyItemParams,
    body: DeTemplateOverridePut,
    response: { status: 200, schema: DeTemplateOverride },
    errors: [],
    emits: ["de_template_override.changed"],
  }),

  // DC-05
  "operator.deploys.overrideVerification": defineRoute({
    ...console_,
    id: "operator.deploys.overrideVerification",
    method: "post",
    path: `${op}/deploys/{deploy_id}/verification-override`,
    summary: "Pilot override for a version without a verification skill (shows unverified)",
    access: "operator",
    params: DeployParams,
    body: VerificationOverrideCreate,
    idempotency: "required",
    response: { status: 201, schema: VerificationOverride },
    errors: ["verification_override_refused", "deploy_state_conflict"],
    emits: ["verification.overridden"],
  }),

  // DC-06 model credentials (widened ADR-0020; every path is off until the founder decides)
  "modelCredentials.list": defineRoute({
    ...credentials,
    id: "modelCredentials.list",
    method: "get",
    path: `${co}/model-credentials`,
    tag: "model-credentials",
    summary: "The caller's own credentials, and the company's for owners and admins",
    access: "member",
    params: CompanyParams,
    query: ModelCredentialListQuery,
    response: { status: 200, schema: page(ModelCredential) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "modelCredentials.create": defineRoute({
    ...credentials,
    id: "modelCredentials.create",
    method: "post",
    path: `${co}/model-credentials`,
    tag: "model-credentials",
    summary: "Add an API key (sealed) or link a CLI subscription (no token), with the attestation",
    access: "member",
    params: CompanyParams,
    body: ModelCredentialCreate,
    idempotency: "required",
    response: { status: 201, schema: ModelCredentialCreated },
    // MODEL-KEYS-PLANS: `conflict` — the company already holds a live key of that provider.
    errors: ["credential_path_disabled", "attestation_required", "conflict"],
    emits: ["model_credential.added", "subscription_attestation.recorded"],
  }),
  "modelCredentials.attest": defineRoute({
    ...credentials,
    id: "modelCredentials.attest",
    method: "post",
    path: `${co}/model-credentials/{id}/attestations`,
    tag: "model-credentials",
    summary: "Re-attest the provider terms (plan or terms changed); the old attestation ends",
    access: "member",
    params: CompanyItemParams,
    body: AttestationInput,
    idempotency: "required",
    response: { status: 201, schema: SubscriptionAttestation },
    errors: ["attestation_required", "credential_path_disabled", "conflict"],
    emits: ["subscription_attestation.recorded", "subscription_attestation.revoked"],
  }),
  "modelCredentials.revoke": defineRoute({
    ...credentials,
    id: "modelCredentials.revoke",
    method: "post",
    path: `${co}/model-credentials/{id}/revoke`,
    tag: "model-credentials",
    summary: "Revoke a credential (a sealed key is destroyed; new runs on it are refused)",
    access: "member",
    params: CompanyItemParams,
    body: ModelCredentialRevoke,
    idempotency: "optional",
    response: { status: 200, schema: ModelCredential },
    errors: ["conflict"],
    emits: ["model_credential.revoked", "subscription_attestation.revoked"],
  }),
  "modelCredentials.update": defineRoute({
    ...credentials,
    id: "modelCredentials.update",
    method: "patch",
    path: `${co}/model-credentials/{id}`,
    tag: "model-credentials",
    summary: "Rename a credential or change its plan-login details (never its key)",
    access: "member",
    params: CompanyItemParams,
    body: ModelCredentialPatch,
    response: { status: 200, schema: ModelCredential },
    errors: ["conflict", "credential_path_disabled"],
    emits: ["model_credential.updated"],
  }),

  // DC-07 verdicts and decision log (ADR-0019)
  "runs.recordVerdict": defineRoute({
    ...runPost("runs.recordVerdict", "verdict", "Record the run's verdict (one per run, depth 0)"),
    access: "run_token",
    params: CompanyItemParams,
    body: VerdictRecord,
    idempotency: "required",
    response: { status: 200, schema: Run },
    errors: [
      "run_not_owned",
      "run_not_active",
      "verdict_already_recorded",
      "verdict_from_worker",
      "evidence_out_of_scope",
    ],
    emits: ["run.verdict_recorded"],
  }),
  "runs.recordDecisions": defineRoute({
    ...runPost("runs.recordDecisions", "decisions", "Append decision-log rows (≤ 50, append-only)"),
    access: "run_token",
    params: CompanyItemParams,
    body: DecisionsAppend,
    idempotency: "required",
    response: { status: 200, schema: DecisionsAppended },
    errors: ["run_not_owned", "run_not_active", "decision_seq_conflict", "evidence_out_of_scope"],
    emits: ["run.decision_recorded"],
  }),
  "runs.escalatePremise": defineRoute({
    ...runPost(
      "runs.escalatePremise",
      "premise",
      "Escalate the shared assumption of two failed fixes",
    ),
    access: "run_token",
    params: CompanyItemParams,
    body: PremiseEscalate,
    idempotency: "required",
    response: { status: 200, schema: Run },
    errors: ["run_not_owned", "run_not_active", "premise_not_shown", "premise_escalated"],
    emits: ["run.premise_escalated", "run.waiting"],
  }),
  "runs.resume": defineRoute({
    ...runPost(
      "runs.resume",
      "resume",
      "The supervisor resumes a refused run (clears its verdict)",
    ),
    access: "member",
    params: CompanyItemParams,
    body: RunResume,
    idempotency: "optional",
    response: { status: 200, schema: Run },
    errors: ["run_state_conflict"],
    emits: ["run.resumed", "run.decision_recorded"],
  }),
  "runs.decisions": defineRoute({
    ...agents,
    id: "runs.decisions",
    method: "get",
    path: `${co}/runs/{id}/decisions`,
    tag: "runs",
    summary: "A run's decision log (run#viewer)",
    access: "member",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: page(RunDecision) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "digitalEmployees.verifiedRate": defineRoute({
    ...agents,
    id: "digitalEmployees.verifiedRate",
    method: "get",
    path: `${co}/digital-employees/{id}/verified-rate`,
    tag: "digital-employees",
    summary: "The DE's `de.verified_rate` per month (viewers of its runs)",
    access: "member",
    params: CompanyItemParams,
    query: VerifiedRateQuery,
    response: { status: 200, schema: VerifiedRate },
    errors: [],
    emits: [],
  }),

  // DC-10 drift audits
  "digitalEmployees.driftAudits": defineRoute({
    ...agents,
    id: "digitalEmployees.driftAudits",
    method: "get",
    path: `${co}/digital-employees/{id}/drift-audits`,
    tag: "digital-employees",
    summary: "The DE's drift audits",
    access: "manager",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: page(DriftAudit) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "digitalEmployees.requestDriftAudit": defineRoute({
    ...agents,
    id: "digitalEmployees.requestDriftAudit",
    method: "post",
    path: `${co}/digital-employees/{id}/drift-audits`,
    tag: "digital-employees",
    summary: "Request a drift audit (manual, or the target system changed)",
    access: "manager",
    params: CompanyItemParams,
    body: DriftAuditRequest,
    idempotency: "optional",
    response: { status: 201, schema: DriftAudit },
    errors: ["conflict"],
    emits: ["drift_audit.scheduled"],
  }),

  // DC-11 self-serve deploy
  "agents.selfServeTemplates": defineRoute({
    ...agents,
    id: "agents.selfServeTemplates",
    method: "get",
    path: `${co}/agent-templates`,
    tag: "deploys",
    summary: "The empty agent and the basic templates this company may deploy itself",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(SelfServeTemplate) }) },
    errors: [],
    emits: [],
  }),
  "agents.deploySelfServe": defineRoute({
    ...agents,
    id: "agents.deploySelfServe",
    method: "post",
    path: `${co}/deploys/self-serve`,
    tag: "deploys",
    summary: "Deploy an empty agent or a basic template (owner or admin; no approval state)",
    access: "manager",
    params: CompanyParams,
    body: SelfServeDeploy,
    idempotency: "required",
    response: { status: 201, schema: Deploy },
    errors: [
      "agent_mode_managed",
      "bundle_refused",
      "deploy_already_open",
      "container_owned",
      "container_not_imported",
      "profile_name_conflict",
    ],
    // DC-11: standing orders or a model map in the body become the DE's override (02
    // `de_template_override`), so a later redeploy keeps them.
    emits: ["deploy.staged", "de_template_override.changed"],
  }),
} as const;
